"use client"

import type React from "react"
import { useRef, useEffect, useCallback, useState } from "react"
import { useSpring, animated } from "@react-spring/web"
import { useDrag } from "@use-gesture/react"

export type WheelProps = {
  items: string[];
  selected: string;
  onSelect: (value: string) => void;
  width?: number;
  itemHeight?: number;
}

const ITEM_HEIGHT = 35;
const VISIBLE_ROWS = 3;
const DRAG_THRESHOLD = 5;

const Wheel: React.FC<WheelProps> = ({ items, selected, onSelect, width = 48, itemHeight = ITEM_HEIGHT }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const dragStateRef = useRef({
    startY: 0,
    isDragging: false
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        ("ontouchstart" in window && window.innerWidth < 768),
      );
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const containerHeight = itemHeight * VISIBLE_ROWS;
  const centerOffset = containerHeight / 2 - itemHeight / 2;

  // Find selected index
  const selectedIndex = items.indexOf(selected);
  const validSelectedIndex = selectedIndex >= 0 ? selectedIndex : 0;

  // Animation state
  const [{ y }, api] = useSpring(() => ({
    y: -(validSelectedIndex * itemHeight - centerOffset),
    config: {
      tension: 300,
      friction: 30
    },
  }));

  // Update position when selected item changes externally
  useEffect(() => {
    const newIndex = items.indexOf(selected);
    if (newIndex >= 0) {
      api.start({
        y: -(newIndex * itemHeight - centerOffset),
        immediate: false,
      });
    }
  }, [selected, items, itemHeight, centerOffset, api]);

  // Snap to nearest item
  const snapToNearest = useCallback(
    (currentY: number, _velocity = 0) => {
      const rawIndex = Math.round((centerOffset - currentY) / itemHeight);
      const clampedIndex = Math.max(0, Math.min(items.length - 1, rawIndex));

      const targetY = -(clampedIndex * itemHeight - centerOffset);
      api.start({
        y: targetY,
        immediate: false
      });

      const newValue = items[clampedIndex];
      if (newValue && newValue !== selected) {
        onSelect(newValue);
      }
    },
    [items, itemHeight, centerOffset, api, selected, onSelect]
  );

  const bind = useDrag(
    ({ offset: [, oy], last, velocity: [, vy] }) => {
      const minY = -(items.length - 1) * itemHeight + centerOffset;
      const clampedY = Math.max(minY, Math.min(centerOffset, oy));

      if (!last) {
        api.start({
          y: clampedY,
          immediate: true,
        });
      } else {
        // Add momentum based on velocity
        const momentum = vy * 50;
        snapToNearest(clampedY + momentum, vy);
      }
    },
    {
      from: () => [0, y.get()],
      axis: "y",
      rubberband: false,
      filterTaps: true,
      enabled: !isMobile // Only enable for desktop
    },
  )

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMobile) return;

    const el = containerRef.current;
    if (!el) return;

    let startY = 0;
    let startTime = 0;
    let lastY = 0;
    let lastTime = 0;
    let isDragging = false;
    let startScrollY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;

      e.preventDefault();
      startY = touch.clientY;
      lastY = touch.clientY;
      startTime = Date.now();
      lastTime = startTime;
      startScrollY = y.get();

      dragStateRef.current = {
        startY: touch.clientY,
        isDragging: false
      };

      setIsDragging(false);
    }

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0]
      if (!touch) return;

      const delta = Math.abs(touch.clientY - dragStateRef.current.startY);

      if (delta > DRAG_THRESHOLD && !dragStateRef.current.isDragging) {
        dragStateRef.current.isDragging = true;
        setIsDragging(true);
      }

      if (!dragStateRef.current.isDragging) return;
      e.preventDefault()

      const currentY = touch.clientY;
      const deltaY = (currentY - startY) * 0.5; // Reduced sensitivity by applying 0.5 multiplier to deltaY
      const currentTime = Date.now();

      lastY = currentY;
      lastTime = currentTime;

      const newY = startScrollY + deltaY;
      const minY = -(items.length - 1) * itemHeight + centerOffset;
      const clampedY = Math.max(minY, Math.min(centerOffset, newY));

      api.start({
        y: clampedY,
        immediate: true,
      });
    }

    const handleTouchEnd = (e: TouchEvent) => {
      // ✅ FIX: TAP detection
      if (!dragStateRef.current.isDragging) {
        const touch = e.changedTouches[0];
        if (touch) {
          const rect = el.getBoundingClientRect();
          const yPos = touch.clientY - rect.top;
          const index = Math.round((yPos - centerOffset) / itemHeight);

          const clampedIndex = Math.max(0, Math.min(items.length - 1, index));
          const value = items[clampedIndex];

          if (value) {
            onSelect(value);
          }
        }

      } else {
        e.preventDefault();

        const endTime = Date.now();
        const timeDelta = Math.max(1, endTime - lastTime);
        const yDelta = lastY - startY;

        const velocity = yDelta / timeDelta;
        const cappedVelocity = Math.max(-2, Math.min(2, velocity));
        const momentum = Math.abs(cappedVelocity) > 0.5 ? cappedVelocity * 20 : 0;
        const currentY = y.get();

        snapToNearest(currentY + momentum, velocity);
      }

      dragStateRef.current.isDragging = false;
      setIsDragging(false);
    }

    el.addEventListener("touchstart", handleTouchStart, { passive: false });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd, { passive: false });

    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
    }
  }, [isMobile, y, api, items, itemHeight, centerOffset, snapToNearest, onSelect]);

  // Mouse wheel support
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const currentIndex = items.indexOf(selected);
      const step = e.deltaY > 0 ? 1 : -1;
      const newIndex = Math.max(0, Math.min(items.length - 1, currentIndex + step));

      if (newIndex !== currentIndex) {
        onSelect(items[newIndex] as string);
      }
    }

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [items, selected, onSelect]);

  const getItemStyle = (index: number) => {
    const isSelected = index === selectedIndex
    const distance = Math.abs(index - selectedIndex)

    if (isSelected) {
      return {
        opacity: 1,
        transform: "scale(1)",
        color: "#ffffff",
        fontWeight: "600",
      }
    }

    const opacity = Math.max(0.3, 1 - distance * 0.4)
    const scale = Math.max(0.85, 1 - distance * 0.1)

    return {
      opacity,
      transform: `scale(${scale})`,
      color: "#6B7280FF",
      fontWeight: "400"
    }
  }

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden select-none"
      style={{
        height: containerHeight,
        width: `${width}px`,
        touchAction: "none" // Prevent default touch actions on mobile
      }}
      data-dialog-content
    >
      <div
        className="absolute left-0 right-0 pointer-events-none border-none"
        style={{
          top: centerOffset,
          height: itemHeight,
          border: "none",
          boxShadow: "rgb(80 92 98 / 60%) 0px 2px 0px 0px inset, rgb(80 92 98 / 60%) 0px -2px 0px 0px inset"
        }}
      />

      <animated.div
        {...(isMobile ? {} : bind())} // Only apply drag binding for desktop
        style={{
          transform: y.to((v: number) => `translateY(${v}px)`),
          cursor: isMobile ? "default" : "grab",
          WebkitUserSelect: "none",
          userSelect: "none",
        }}
        className={isMobile ? "" : "active:cursor-grabbing"}
      >
        {items.map((item, index) => (
          <div
            key={`${index}-${item}`}
            className="flex items-center justify-center text-center transition-all duration-150"
            style={{
              height: itemHeight,
              ...getItemStyle(index),
            }}
            onClick={() => onSelect(item)}
          >
            {item}
          </div>
        ))}
      </animated.div>
    </div>
  )
}

Wheel.displayName = "Wheel"
export { Wheel }
